import {
  formatDistanceToNow,
  isAfter,
  subDays,
  subHours,
  subMonths,
} from 'date-fns';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router';
import { useSnapshot } from 'valtio';

import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import Text from '@cartwhl/ui/base/Text';
import ChevronDownIcon from '@cartwhl/ui/icons/ChevronDownIcon';
import ComicThumbnail from '@cartwhl/ui/icons/ComicThumbnail';
import WarningIcon from '@cartwhl/ui/icons/WarningIcon';
import Button from '@cartwhl/ui/input/Button';

import getBatches from '@cartwhl/common/api/getBatches';
import type { BatchResponse, BatchStatus } from '@cartwhl/common/api/getBatch';
import { userStore } from '../../../stores/global';

import BatchMotionThumbnailLoadingChip from '../../shared/BatchMotionThumbnailLoadingChip';
import RecentBatchJobsSectionHeader from '../../shared/RecentBatchJobsSectionHeader';

/** Matches `ALL_PROJECTS_LIMIT` in studio project store — same page size as Projects. */
const COMIC_BATCHES_PAGE_SIZE = 8;

/** How often to re-poll while any visible job is still in-flight. */
const POLL_INTERVAL_MS = 30_000;

type ViewMode = 'grid' | 'list';

const SECTION_ORDER = [
  'Created this week',
  'Created this month',
  'Older jobs',
] as const;

type ComicSectionTitle = (typeof SECTION_ORDER)[number];

function sortBatchesByCreatedDesc(list: BatchResponse[]): BatchResponse[] {
  return [...list].sort(
    (a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

function groupComicBatchesByCreatedAt(
  jobs: BatchResponse[],
): Record<ComicSectionTitle, BatchResponse[]> {
  const buckets: Record<ComicSectionTitle, BatchResponse[]> = {
    'Created this week': [],
    'Created this month': [],
    'Older jobs': [],
  };
  const now = new Date();
  const weekCut = subDays(now, 7);
  const monthCut = subMonths(now, 1);

  for (const job of jobs) {
    const d = new Date(job.createdAt);
    if (isAfter(d, weekCut)) buckets['Created this week'].push(job);
    else if (isAfter(d, monthCut)) buckets['Created this month'].push(job);
    else buckets['Older jobs'].push(job);
  }

  const sortDesc = (a: BatchResponse, b: BatchResponse) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  buckets['Created this week'].sort(sortDesc);
  buckets['Created this month'].sort(sortDesc);
  buckets['Older jobs'].sort(sortDesc);

  return buckets;
}

function isComicBatchProcessing(status: BatchStatus): boolean {
  return (
    status === 'VALIDATING' ||
    status === 'QUEUED' ||
    status === 'IN_PROGRESS'
  );
}

/** Stale API status: hide in-progress dim/spinner on history after 48h. */
function isBatchCreatedOlderThanTwoDays(createdAt: string): boolean {
  return new Date(createdAt) < subHours(new Date(), 48);
}

/** Batch finished with no successes (history list uses API batch status only). */
function isComicBatchFailedHistory(status: BatchStatus): boolean {
  return status === 'FAILED' || status === 'REJECTED';
}

function ComicBatchThumbnail({
  compact,
  processing = false,
  failed = false,
}: {
  compact?: boolean;
  processing?: boolean;
  failed?: boolean;
}) {
  const dimmed = processing || failed;

  return (
    <div className="bg-gray-30 dark:bg-gray-70 relative flex h-full w-full items-center justify-center overflow-hidden">
      <ComicThumbnail
        processing={false}
        className={
          compact
            ? 'h-[min(92%,58px)] w-auto text-gray-45 dark:text-gray-55'
            : 'h-[min(92%,132px)] w-auto sm:h-[min(92%,152px)] text-gray-45 dark:text-gray-55'
        }
      />
      {dimmed ? (
        <div
          className="pointer-events-none absolute inset-0 z-1 bg-black/40 dark:bg-black/45"
          aria-hidden
        />
      ) : null}
      {processing ? <BatchMotionThumbnailLoadingChip compact={compact} /> : null}
      {failed && !processing ? (
        <div
          className={`pointer-events-none absolute right-2 bottom-2 z-10 shrink-0 ${compact ? 'h-8 w-8' : 'h-9 w-9'}`}
          aria-hidden
        >
          <div className="absolute inset-0 rounded-lg bg-white" />
          <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-red/10 dark:bg-red/10">
            <WarningIcon
              className={`text-red shrink-0 ${compact ? 'h-4 w-4' : 'h-5 w-5'}`}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function ComicBatchSection({
  title,
  amount,
  viewMode,
  children,
}: {
  title: ComicSectionTitle;
  amount: number;
  viewMode: ViewMode;
  children: ReactNode;
}) {
  const [collapsed, setCollapsed] = useState(false);

  if (amount === 0) return null;

  return (
    <div className="flex flex-col gap-5 py-8">
      <div className="flex items-center justify-between px-2">
        <Text as="h3" variant="studio/body/md" md:variant="studio/subtitle">
          {title}{' '}
          <span className="text-gray-45 dark:text-gray-60">({amount})</span>
        </Text>
        <Button
          variant="tertiary"
          size="sm"
          icon={<ChevronDownIcon className={collapsed ? '-rotate-90' : ''} />}
          onClick={() => setCollapsed(!collapsed)}
        />
      </div>
      {!collapsed && (
        <div
          className={
            viewMode === 'list'
              ? 'flex flex-col gap-2'
              : 'grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 xl:px-0'
          }
        >
          {children}
        </div>
      )}
    </div>
  );
}

function JobCard({
  job,
  viewMode,
  onClick,
}: {
  job: BatchResponse;
  viewMode: ViewMode;
  onClick: () => void;
}) {
  const title = job.batchName || job.batchID;
  const itemPhrase = `${job.numTotalJobs} ${job.numTotalJobs === 1 ? 'item' : 'items'}`;
  const createdAgo = `Created ${formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}`;

  const processing =
    isComicBatchProcessing(job.status) &&
    !isBatchCreatedOlderThanTwoDays(job.createdAt);
  const failed = isComicBatchFailedHistory(job.status);

  if (viewMode === 'list') {
    return (
      <div
        className="group flex cursor-pointer select-none items-center gap-4 rounded-xl px-2 py-2 transition-colors hover:bg-gray-15 dark:hover:bg-gray-75"
        onClick={onClick}
      >
        <div className="relative aspect-video w-24 shrink-0 overflow-hidden rounded-lg">
          <ComicBatchThumbnail
            compact
            processing={processing}
            failed={failed}
          />
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <Text variant="studio/body/sm" className="text-secondary truncate font-medium">
            {title}
          </Text>
          <Text variant="studio/body/sm" className="text-tertiary">
            Comic batch - {itemPhrase}
          </Text>
          <Text variant="studio/body/sm" className="text-tertiary">
            {createdAgo}
          </Text>
        </div>
      </div>
    );
  }

  return (
    <div
      className="group flex cursor-pointer select-none flex-col gap-3 rounded-2xl p-2 transition-colors hover:bg-gray-15 dark:hover:bg-gray-75"
      onClick={onClick}
    >
      <div className="relative aspect-video overflow-hidden rounded-xl">
        <ComicBatchThumbnail processing={processing} failed={failed} />
      </div>
      <div className="flex min-w-0 flex-col gap-0.5 px-2 pb-1">
        <Text variant="studio/body/sm" className="text-secondary truncate font-medium">
          {title}
        </Text>
        <Text variant="studio/body/sm" className="text-tertiary">
          Comic batch - {itemPhrase}
        </Text>
        <Text variant="studio/body/sm" className="text-tertiary">
          {createdAgo}
        </Text>
      </div>
    </div>
  );
}

export default function RecentComicJobs() {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [jobs, setJobs] = useState<BatchResponse[]>([]);
  const [nextToken, setNextToken] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const snap = useSnapshot(userStore.state);
  const apiKey = snap.apiKey.data?.apiKey ?? '';
  const apiKeyRef = useRef(apiKey);
  apiKeyRef.current = apiKey;

  const loadPage = useCallback(async (token?: string) => {
    const keyAtStart = apiKeyRef.current;
    if (!keyAtStart) return;
    try {
      const res = await getBatches(keyAtStart, {
        type: 'COMIC',
        limit: COMIC_BATCHES_PAGE_SIZE,
        nextToken: token,
      });
      if (apiKeyRef.current !== keyAtStart) return;

      setNextToken(res.nextToken);
      setJobs((prev) => {
        if (!token) return sortBatchesByCreatedDesc(res.items);
        const seen = new Set(prev.map((j) => j.batchID));
        const merged = [...prev];
        for (const item of res.items) {
          if (!seen.has(item.batchID)) {
            seen.add(item.batchID);
            merged.push(item);
          }
        }
        return sortBatchesByCreatedDesc(merged);
      });
    } catch {
      if (apiKeyRef.current !== keyAtStart) return;
      if (!token) {
        setJobs([]);
        setNextToken(undefined);
      }
    }
  }, []);

  useEffect(() => {
    if (!apiKey) return;
    setLoading(true);
    setNextToken(undefined);
    setJobs([]);
    void loadPage().finally(() => setLoading(false));
  }, [apiKey, loadPage]);

  const loadMore = useCallback(() => {
    if (!apiKey || !nextToken || loadingMore) return;
    setLoadingMore(true);
    void loadPage(nextToken)
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [apiKey, nextToken, loadingMore, loadPage]);

  /** Silently re-fetch the first page and merge status updates into existing items. */
  const refreshStatuses = useCallback(async () => {
    const key = apiKeyRef.current;
    if (!key) return;
    try {
      const res = await getBatches(key, {
        type: 'COMIC',
        limit: COMIC_BATCHES_PAGE_SIZE,
      });
      if (apiKeyRef.current !== key) return;
      setJobs((prev) => {
        const updatedById = new Map(res.items.map((j) => [j.batchID, j]));
        const merged = prev.map((j) => updatedById.get(j.batchID) ?? j);
        const existingIds = new Set(prev.map((j) => j.batchID));
        for (const item of res.items) {
          if (!existingIds.has(item.batchID)) merged.push(item);
        }
        return sortBatchesByCreatedDesc(merged);
      });
    } catch {
      // silently ignore — stale data is fine
    }
  }, []);

  /** Poll while any loaded job is still actively processing (and not stale). */
  const hasInFlightJobs = jobs.some(
    (j) =>
      isComicBatchProcessing(j.status) &&
      !isBatchCreatedOlderThanTwoDays(j.createdAt),
  );

  useEffect(() => {
    if (!hasInFlightJobs || !apiKey) return;
    const id = setInterval(() => void refreshStatuses(), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [hasInFlightJobs, apiKey, refreshStatuses]);

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return jobs;
    const q = searchQuery.toLowerCase();
    return jobs.filter(
      (j) =>
        (j.batchName ?? '').toLowerCase().includes(q) ||
        j.batchID.toLowerCase().includes(q),
    );
  }, [jobs, searchQuery]);

  const grouped = useMemo(
    () => groupComicBatchesByCreatedAt(filtered),
    [filtered],
  );

  const hasAnyAfterFilter = filtered.length > 0;
  const doneLoadingAll = nextToken === undefined;
  const showInfiniteTrigger =
    !loading && apiKey && (jobs.length > 0 || !doneLoadingAll);

  return (
    <div className="container mx-auto px-4 pt-10 pb-16 sm:px-6 lg:px-8">
      <RecentBatchJobsSectionHeader
        title="Recent Comic jobs"
        searchInputName="search-comic-jobs"
        libraryTypeFilter="comic-batch"
        showControls={jobs.length > 0 && !loading}
        searchQuery={searchQuery}
        onSearchQueryChange={setSearchQuery}
        viewMode={viewMode}
        onToggleViewMode={() =>
          setViewMode(viewMode === 'grid' ? 'list' : 'grid')
        }
      />

      {loading ? (
        <Text variant="studio/body/md" className="text-secondary py-4">
          Loading…
        </Text>
      ) : jobs.length === 0 ? (
        <Text variant="studio/body/md" className="text-secondary py-4">
          No comic jobs yet. Run your first comic batch above.
        </Text>
      ) : !hasAnyAfterFilter ? (
        <Text variant="studio/body/md" className="text-secondary py-4">
          No jobs match your search.
        </Text>
      ) : (
        <div className="divide-gray-30 dark:divide-gray-75 flex flex-col divide-y">
          {SECTION_ORDER.map((sectionKey) => (
            <ComicBatchSection
              key={sectionKey}
              title={sectionKey}
              amount={grouped[sectionKey].length}
              viewMode={viewMode}
            >
              {grouped[sectionKey].map((job) => (
                <JobCard
                  key={job.batchID}
                  job={job}
                  viewMode={viewMode}
                  onClick={() => navigate(`/comic/${job.batchID}`)}
                />
              ))}
            </ComicBatchSection>
          ))}
        </div>
      )}

      {showInfiniteTrigger ? (
        <div className="flex justify-center py-6">
          <InfiniteLoadingTrigger
            loading={loadingMore}
            done={doneLoadingAll}
            onEnter={loadMore}
          />
        </div>
      ) : null}
    </div>
  );
}
